/**
 * @cortex/shared — sunucu, web hub ve masaustu arasindaki SOZLESME.
 *
 * Buradaki tipler API'nin disa acik yuzeyidir. Degistirmek isteyen once
 * "hangi istemciler kirilir?" sorusunu cevaplamali. Masaustu Electron tarafi
 * duz JavaScript oldugu icin bu tipleri derlemez — ama alan adlari birebir
 * ayni olmak zorundadir.
 *
 * ISIMLENDIRME: burada "Kode", "ekran" veya "oyun" gecmez. Platform bir
 * marketplace; Kode LED yalnizca ilk musteridir. Genel kavramlar:
 *   Product  — satilan sey (VR oyun, immersive art, LED servisi, arac...)
 *   Endpoint — urunun uzerinde calistigi yonetilen cikti
 *   Session  — bir urunun bir endpoint uzerinde planlanmis calismasi
 */

// ─── Roller ve yetki ─────────────────────────────────────────────────────────

/** Wondeon personeli | musteri yoneticisi | musteri operatoru */
export type Role = 'owner' | 'customer_admin' | 'customer_user';

// ─── Wondeon personel yasam dongusu ────────────────────────────────────────

export type WorkforceMemberStatus = 'active' | 'suspended';
export type WorkforceInvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

export interface WorkforceMember {
  id: string;
  name: string;
  email: string;
  status: WorkforceMemberStatus;
  revision: number;
  createdAt: string;
  lastLoginAt?: string | null;
  roles?: Array<{ id: string; name: string }>;
}

export interface WorkforceInvitation {
  id: string;
  email: string;
  status: WorkforceInvitationStatus;
  revision: number;
  expiresAt: string;
  createdAt: string;
  roleIds: string[];
}

// ─── Wondeon personel rol ve izin şablonları ───────────────────────────────

export const HUB_ADMIN_ACTIONS = [
  'admin.overview.read',
  'admin.accounts.read',
  'admin.accounts.write',
  'admin.catalog.read',
  'admin.catalog.write',
  'admin.catalog.publish',
  'admin.catalog.revoke',
  'admin.support.read',
  'admin.support.write',
  'admin.operations.read',
  'admin.operations.write',
  'admin.website.read',
  'admin.website.write',
  'admin.website.publish',
  'admin.website.rollback',
  'admin.system.read',
  'admin.system.write',
  'admin.audit.read',
  'staff.manage',
] as const;

export type HubAdminAction = (typeof HUB_ADMIN_ACTIONS)[number];

// ─── Cortex Dev yetki sozlesmesi ─────────────────────────────────────────────

/**
 * Cortex Dev'in sunucuda tek tek yetkilendirilen atomik eylemleri.
 *
 * Liste bir guvenlik siniridir: sozlukte olmayan degerler istemci daha yeni
 * olsa bile sunucuda fail-closed reddedilir.
 */
export const DEV_ACTIONS = [
  'dev.access',
  'project.create',
  'resource.discover',
  'source.read',
  'source.branch.write',
  'source.main.merge',
  'artifact.upload',
  'artifact.download',
  'qa.submit',
  'qa.approve',
  'release.publish',
  'release.rollback',
  'engine.deploy',
  'engine.publish',
  'rigmo.diagnostic.read',
  'dev-app.deploy',
  'dev-app.update.read',
  'dev-app.update.apply',
  'task.read',
  'task.create',
  'task.update',
  'task.assign',
  'task.transition',
  'note.read',
  'note.create',
  'admin.manage',
] as const;

export type DevAction = (typeof DEV_ACTIONS)[number];

export const STAFF_ACTIONS = [...HUB_ADMIN_ACTIONS, ...DEV_ACTIONS] as const;
export type StaffAction = (typeof STAFF_ACTIONS)[number];

export const DEV_SCOPE_KINDS = ['global', 'category', 'project', 'engine'] as const;
export type DevScopeKind = (typeof DEV_SCOPE_KINDS)[number];

export type DevScope =
  | { kind: 'global'; id: null }
  | { kind: Exclude<DevScopeKind, 'global'>; id: string };

export type DevResource =
  | { kind: 'global'; id?: never; categoryId?: never }
  | { kind: 'category'; id: string; categoryId?: never }
  | { kind: 'project'; id: string; categoryId?: string }
  | { kind: 'engine'; id: string; categoryId?: never };

export type DevGrantEffect = 'allow' | 'deny';

export interface StaffRolePermission {
  action: StaffAction;
  effect: DevGrantEffect;
  scope: DevScope;
  revision: number;
}

export interface StaffRole {
  id: string;
  name: string;
  description: string | null;
  systemKey: 'wondeon_super_admin' | null;
  system: boolean;
  archived: boolean;
  revision: number;
  permissions: StaffRolePermission[];
}

export interface EffectiveStaffPermission extends StaffRolePermission {
  source: 'system-role' | 'custom-role' | 'direct';
  roleId: string | null;
  roleName: string | null;
}

export interface EffectiveStaffAccess {
  revision: number;
  roleIds: string[];
  permissions: EffectiveStaffPermission[];
}

export const DEV_ACCESS_REASON_CODES = [
  'staff_onboarding',
  'role_change',
  'security_revocation',
  'offboarding',
  'correction',
] as const;
export type DevAccessReasonCode = (typeof DEV_ACCESS_REASON_CODES)[number];

/** Eylem ve scope bagini korur; actions[] x scopes[] capraz carpimi yetki degildir. */
export interface DevGrant {
  action: DevAction;
  effect: DevGrantEffect;
  scope: DevScope;
  revision: number;
  /** ISO 8601; doluysa bu grant karar hesabina katilmaz. */
  revokedAt: string | null;
}

/** GET /dev/access yalniz halen etkili kayitlari dondurur; gecmis ayri tutulur. */
export type EffectiveDevGrant = Omit<DevGrant, 'revokedAt'> & {
  /** Eski istemci/fixture uyumu için opsiyonel; güncel API her satırda üretir. */
  source?: 'direct' | 'system-role' | 'custom-role';
  roleId?: string | null;
  roleName?: string | null;
};

export interface EffectiveDevAccess {
  /** Kişi bazlı grant/uygunluk CAS revision'ı. */
  revision: number;
  /** Rol assignment/izin snapshot revision'ı; iki revision birleştirilmez. */
  staffRevision?: number;
  effectiveGrants: EffectiveDevGrant[];
}

export type DevAccessDecisionReason =
  | 'ALLOW'
  | 'DEV_ACCESS_REQUIRED'
  | 'EXPLICIT_DENY'
  | 'NO_MATCHING_GRANT'
  | 'UNKNOWN_ACTION'
  | 'UNKNOWN_SCOPE'
  | 'INVALID_GRANT';

export interface DevAccessDecision {
  allowed: boolean;
  reason: DevAccessDecisionReason;
}

/** Sidebar'da gorunebilecek bolumler. Sunucu uretir, istemci birebir cizer. */
export type NavKey =
  | 'home'
  | 'library'
  | 'subscriptions'
  | 'operations'
  | 'account'
  | 'admin';

export type EntitlementKind = 'product' | 'package';

/**
 * Yetkinin odeme durumu.
 *
 * KREDI YALNIZCA 'paid' ve 'trial' durumunda yuklenir — "ucret yatirilmadan
 * kesinlikle kredi verilmiyor" kuralinin sozlesme karsiligi.
 */
export type PaymentStatus = 'pending' | 'paid' | 'trial' | 'refunded' | 'waived';

export interface Entitlement {
  productId: string;
  productSlug: string;
  productName: string;
  /** Ürünün katalog çalışma alanı; sidebar ve route erişiminin kanonik kaynağıdır. */
  category: string;
  kind: EntitlementKind;
  /** Paket uzerinden geldiyse paketin adi — arayuzde "X paketi ile" yazar. */
  viaPackageName: string | null;
  /** ISO 8601. null = suresiz. */
  expiresAt: string | null;
  /** Bu erisim bir DENEME mi? Arayuz rozet ve geri sayim gosterir. */
  isTrial: boolean;
}

// ─── Deneme (trial) ──────────────────────────────────────────────────────────

export interface TrialOffer {
  packageId: string;
  packageSlug: string;
  packageName: string;
  summary: string;
  trialDays: number;
  trialCredits: number;
  /** Bu hesap denemeye uygun mu? */
  eligible: boolean;
  /** eligible=false ise KULLANICIYA gosterilecek sebep. */
  reason: string | null;
}

export interface TrialStatus {
  entitlementId: string;
  packageName: string;
  expiresAt: string;
  daysLeft: number;
  /** Son 3 gun — arayuz uyari gosterir. */
  endingSoon: boolean;
}

export interface CreditBalance {
  /** Bu bakiye yalnız aynı adlı katalogda harcanabilir. */
  catalog: string;
  balance: number;
  /**
   * Su an tek birim var: 1 kredi = QR ile baslatilan 1 oyun turu.
   * (Agustos 2026'ya kadar `endpoint-hour` idi; satis modeli dokumaniyla
   * hizalandi — bkz. server/src/modules/credits/policy.ts)
   */
  unit: 'round';
  /** Bu katalogdaki yürürlükteki paketlerin verdiği kredi. */
  granted: number;
}

export type ThemePreference = 'dark' | 'light' | 'system';
export type LanguagePreference = 'tr' | 'en';

export interface AccountBranding {
  displayName?: string;
  logoUrl?: string;
  accentColor?: '#534ab7' | '#006b5f' | '#9a3412' | '#1d4ed8' | '#7e22ce' | '#9f1239';
}

// ─── Hukuki metin sürümleri ─────────────────────────────────────────────────

/**
 * Onay kayıtları metin sürümüne bağlıdır. Metin esaslı biçimde değişirse bu
 * değer de değiştirilir; böylece yeni sürüm kullanıcıya yeniden gösterilir.
 */
export const KVKK_NOTICE_VERSION = '2026-08-23';
export const TERMS_VERSION = '2026-08-23';

export type LegalDocument = 'kvkk_notice' | 'terms';
export type LegalAcceptanceSource = 'registration' | 'google' | 'invitation';

/** Kamu web sitesinden Hub başvurusuna taşınabilen kanonik kataloglar. */
export const PUBLIC_CATALOGS = [
  'Leds Play',
  'VR Deneyimleri',
  'Immersive Digital Art',
  'AR Deneyimleri',
] as const;
export type PublicCatalog = (typeof PUBLIC_CATALOGS)[number];

/** GET /me — sidebar, rotalar ve katalog filtresi bunu kullanir. */
export interface MeResponse {
  adminCapabilities?:{createCustomer:boolean};
  user: {
    id: string;
    email: string;
    name: string;
    role: Role;
    emailVerified: boolean;
    theme: ThemePreference;
    language: LanguagePreference;
  };
  account: {
    id: string;
    name: string;
    deviceLimit: number;
    /** Planlanabilecek en kisa oturum (dakika). Hesap bazinda ayarlanir. */
    minSessionMinutes: number;
    branding: AccountBranding;
  };
  nav: NavKey[];
  entitlements: Entitlement[];
  /** Tek toplam yoktur; her katalog kendi bağımsız kredi cüzdanını taşır. */
  credits: CreditBalance[];
  /** Hesapta en az bir endpoint varsa "Operasyon" bolumu anlamli olur. */
  endpointCount: number;
  /** Müşteriye paylaşılmış teklifler; sidebar rozetini ve Abonelikler görünürlüğünü besler. */
  pendingOfferCount: number;
  /** Kurum doğrulama akışı. Eski/onaylı hesaplarda null olabilir. */
  activation: {
    status: 'pending_review' | 'contact_required' | 'meeting_scheduled' | 'approved' | 'rejected';
    sourceCatalog: string | null;
    sourceProductSlug: string | null;
    contactPreference: 'phone' | 'meet' | null;
  } | null;
  /**
   * Yururlukteki denemeler. Bos degilse arayuz geri sayim seridi gosterir —
   * denemenin sessizce bitmesi, musterinin bir sabah sistemin calismadigini
   * gormesi demektir.
   */
  trials: TrialStatus[];
}

// ─── Katalog ─────────────────────────────────────────────────────────────────

export type ProductKind =
  | 'vr-game'
  | 'immersive-art'
  | 'motion-track'
  | 'installation'
  | 'led-service'
  | 'tool';

/** Urunun istemcide nasil davranacagi. */
export type ProductDelivery =
  /** Cortex indirir ve baslatir */
  | 'desktop-install'
  /** Yalnizca web/panel uzerinden yonetilir (or. LED servisi) */
  | 'web-only';

export type ContentLanguage = 'tr' | 'en';

/** Ürün metinlerinin kanonik TR kaydıyla birlikte taşınan dil varyantları. */
export interface ProductLocalizedFields {
  name: string;
  summary: string;
  description: string;
}

export type ProductLocalizedContent = Partial<
  Record<ContentLanguage, Partial<ProductLocalizedFields>>
>;

export interface CatalogItem {
  id: string;
  slug: string;
  name: string;
  kind: ProductKind;
  delivery: ProductDelivery;
  category: string;
  /** Kart kapagi (dikey ~3:4). Masaustu bunu yerel diske onbellekler. */
  coverUrl: string | null;
  /** Genis hero gorseli (~16:6) — slayt ve kategori basligi icin. */
  bannerUrl: string | null;
  /** Kucuk liste gorseli — kompakt satirlar icin. Yoksa kapaga duser. */
  thumbUrl: string | null;
  /** Yayindaki surum. null = henuz yayinlanmadi. */
  latestVersion: string | null;
  sizeBytes: number | null;
  summary: string;
  /**
   * Hesabin bu urune erisimi var mi? Katalog HERKESE gosterilir (vitrin),
   * ama kilit isareti ve "Indir" butonu buna gore degisir.
   */
  entitled: boolean;
  /** Yetkinin bitis tarihi — yaklasiyorsa arayuzde uyari cikar. */
  entitlementExpiresAt: string | null;
  /** Katalogda yeni eklenenleri sunucu verisinden sıralamak için kayıt zamanı. */
  createdAt: string;
}

export interface PackageFinderItem {
  id: string;
  slug: string;
  name: string;
  summary: string;
  catalog: string;
  products: Array<Pick<CatalogItem, 'id' | 'slug' | 'name' | 'kind' | 'delivery' | 'coverUrl' | 'thumbUrl'>>;
}

export interface ProductDetail extends CatalogItem {
  description: string;
  /** Bu urunun calismasi icin endpoint'in saglamasi gereken kosullar. */
  requirements: ProductRequirements;
  releases: ReleaseSummary[];
}

export interface ProductRequirements {
  /** Bos/eksik = kisit yok. */
  endpointKinds?: EndpointKind[] | undefined;
  /** Ornek: ["16:9", "2.16:1"] — endpoint en-boy orani bunlardan biri olmali. */
  aspectRatios?: string[] | undefined;
  minWidth?: number | undefined;
  minHeight?: number | undefined;
  requiresAudio?: boolean | undefined;
  /** Cihaz ölçümü bilinmiyorsa planlama güvenli biçimde reddeder. */
  requiresDedicatedGpu?: boolean | undefined;
  /** CORTEX'in işletim sisteminden bildirdiği ayrılmış ekran belleği. */
  minDedicatedGpuMemoryMb?: number | undefined;
  /** Aktif ekranlardan en az birinin sağlaması gereken yenileme hızı. */
  minRefreshRateHz?: number | undefined;
  /** Son başarılı Hub heartbeat ölçümü için izin verilen üst sınır. */
  maxApiLatencyMs?: number | undefined;
  /** Yönetici açtıysa yetkili müşteri süreli, sunucu jetonlu sunum demosu başlatabilir. */
  liveDemoEnabled?: boolean | undefined;
  /**
   * Ürün detayındaki oynanış/deneyim galerisi. Kapak ve banner'dan ayrıdır;
   * her kayıt gerçek ürüne ait farklı bir sahne olmalıdır.
   */
  galleryUrls?: string[] | undefined;
  /** Künye değerleri iki dilde yönetilir; doğrulanmamış sayısal iddia zorunlu değildir. */
  sessionDuration?: ProductLocalizedLabel | undefined;
  participants?: ProductLocalizedLabel | undefined;
  controlScheme?: ProductLocalizedLabel | undefined;
  /**
   * Oyun adapter'ının gerçekten uyguladığı, Hub'ın güvenli form olarak
   * gösterebildiği ayarlar. Serbest JSON veya JavaScript şeması taşınmaz.
   */
  gameSettings?: DisplayGameSettingDefinition[] | undefined;
}

export interface ProductLocalizedLabel {
  tr?: string | undefined;
  en?: string | undefined;
}

export type DisplayGameSettingKind = 'boolean' | 'number' | 'select' | 'color' | 'text';

export interface DisplayGameSettingOption {
  value: string;
  label: ProductLocalizedLabel;
}

export interface DisplayGameSettingDefinition {
  key: string;
  kind: DisplayGameSettingKind;
  label: ProductLocalizedLabel;
  help?: ProductLocalizedLabel | undefined;
  required?: boolean | undefined;
  defaultValue?: boolean | number | string | undefined;
  min?: number | undefined;
  max?: number | undefined;
  step?: number | undefined;
  options?: DisplayGameSettingOption[] | undefined;
}

export interface ReleaseSummary {
  id: string;
  version: string;
  channel: 'stable' | 'beta';
  sizeBytes: number;
  publishedAt: string | null;
  notes: string;
}

export interface BannerSlide {
  id: string;
  imageUrl: string;
  title: string;
  subtitle: string | null;
  ctaLabel: string | null;
  ctaHref: string | null;
  /** Kucukten buyuge siralama. */
  order: number;
}

export interface BannerLocalizedFields {
  title: string;
  subtitle: string | null;
  ctaLabel: string | null;
}

export type BannerLocalizedContent = Partial<
  Record<ContentLanguage, Partial<BannerLocalizedFields>>
>;

// ─── Kategori satis icerigi ──────────────────────────────────────────────────

/**
 * Bir kategorinin satis anlatimi. Tamami yonetim panelinden duzenlenir;
 * kaynak: docs/WEB_HANDOFF_Satis_Modeli.md.
 *
 * Her bolum opsiyonel — bos birakilan bolum arayuzde CIZILMEZ. Boylece LED
 * Oyunlar'in tam anlatimi ile hazirlanma asamasindaki bir kategorinin tek
 * paragrafi ayni yapida yasar.
 *
 * ── FIYAT YASAGI ─────────────────────────────────────────────────────────────
 * Hicbir alanda tutar yok. `showPrices` acilana kadar arayuz fiyat basmaz;
 * tek eylem cagrisi "Teklif Al". Paket yogunluklari da rakamla degil
 * ("dusuk/orta/yuksek") yazilir — tur adedi fiyatla birlikte aciklanacak.
 */
export interface CategorySales {
  /** Fiyat alanlari gosterilsin mi? Yonetim onayina kadar false. */
  showPrices?: boolean;
  hero?: { title: string; subtitle?: string; ctaLabel?: string };
  /** "Nasil calisir" adimlari. */
  steps?: Array<{ title: string; text: string }>;
  /** Abonelikte gelen modul/yetenek listesi. `extra` = ek hizmet, projelendirilir. */
  modules?: Array<{ name: string; text: string; extra?: boolean }>;
  /** Kredi/ucretlendirme anlatimi. */
  credits?: { intro?: string; bullets: string[] };
  /** S/M/L paket karsilastirmasi. `featured` kart one cikar. */
  packages?: Array<{
    id: string;
    name: string;
    featured?: boolean;
    intensity?: string;
    features: Array<{ label: string; value: string }>;
    ctaLabel?: string;
  }>;
  /** Kurulum/devreye alma zaman cizelgesi. */
  timeline?: { title?: string; steps: string[]; expectations?: string[] };
  /** Ek hizmetler — her biri teklif akisina baglanir. */
  extras?: string[];
  faq?: Array<{ q: string; a: string }>;
  /** Teklif formunun hedefi. Bos ise arayuz kendi teklif baglantisini kullanir. */
  ctaHref?: string;
}

export type CategorySalesLocalizedContent = Partial<Record<ContentLanguage, CategorySales>>;

export interface CategoryContent {
  category: string;
  slug: string;
  published: boolean;
  sales: CategorySales;
  updatedAt: string;
}

// ─── Endpoint'ler ────────────────────────────────────────────────────────────

export type EndpointKind =
  | 'led-screen'
  | 'vr-station'
  | 'projection'
  | 'installation'
  | 'generic';

export type EndpointStatus = 'online' | 'offline' | 'playing';

export interface EndpointCapabilities {
  /** Ornek: "3840x1280" */
  resolution?: string;
  /** Ornek: "3:1". Sunucu resolution'dan da turetebilir. */
  aspectRatio?: string;
  /** Duz dikdortgen olmayan yerlesimler: "L-kose", "kavisli". */
  geometry?: string;
  hasAudio?: boolean;
  orientation?: 'landscape' | 'portrait';
  contentLock?: { locked: boolean; productSlug: string | null };
  /** Müşteri yöneticisinin açıkça verdiği ekran önizleme izni. */
  screenPreviewAllowed?: boolean;
  /** CORTEX sürümü ekran yakalamayı destekliyor mu? */
  screenPreviewSupported?: boolean;
  /** Kişisel veri, seri numarası ve MAC adresi içermez. */
  hardware?: {
    osVersion?: string;
    totalMemoryMb?: number;
    gpus?: Array<{
      name: string;
      driverVersion?: string;
      dedicatedMemoryMb?: number;
    }>;
    displays?: Array<{
      width: number;
      height: number;
      refreshRateHz?: number;
      orientation: 'landscape' | 'portrait';
    }>;
    measuredAt: string;
  };
  /** API erişimi istemci heartbeat süresinden ölçülür; bant genişliği iddiası değildir. */
  network?: {
    reachable: boolean;
    apiLatencyMs?: number;
    measuredAt: string;
  };
}

export interface OperatingHours {
  /** IANA saat dilimi. Ornek: "Europe/Istanbul". */
  tz: string;
  /** "07:00" */
  start: string;
  /** "00:00" — start'tan kucukse gece yarisini asiyor demektir. */
  end: string;
}

export interface Endpoint {
  id: string;
  kind: EndpointKind;
  name: string;
  location: string;
  capabilities: EndpointCapabilities;
  operatingHours: OperatingHours | null;
  status: EndpointStatus;
  /** ISO 8601 — offline ise "son gorulme" olarak gosterilir. */
  lastSeenAt: string | null;
  /** status === 'playing' ise dolu. */
  nowPlaying: {
    sessionId: string;
    productName: string;
    endsAt: string;
  } | null;
  /** CORTEX'in son heartbeat'te bildirdigi kurulu urun surumleri. */
  installedVersions: Record<string, string>;
}

export type EndpointCommandAction =
  | 'install'
  | 'update'
  | 'uninstall'
  | 'configure'
  | 'restart'
  | 'set_volume'
  | 'set_content_lock'
  | 'start_preview'
  | 'stop_preview';
export type EndpointCommandStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface EndpointCommand {
  id: string;
  endpointId: string;
  productId: string | null;
  productSlug: string;
  productName: string;
  action: EndpointCommandAction;
  status: EndpointCommandStatus;
  semanticReceipt?: {
    schemaVersion: number;
    status: 'accepted' | 'rejected';
    appliedConfigSha256?: string;
    adapterId?: string;
    reasonCode?: string;
    appliedAt: string;
  } | null;
  attempts: number;
  message: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

// ─── Bağımsız Display Runtime ────────────────────────────────────────────────

/**
 * Display Shell ile izole oyun sürecinin konuştuğu sürüm. Kırıcı bir değişiklik
 * yeni bir sayı alır; CORTEX farklı sürümleri sessizce birbirine bağlamaz.
 */
export const DISPLAY_TEMPLATE_SCHEMA_VERSION = 1 as const;
export type DisplayTemplateSchemaVersion = typeof DISPLAY_TEMPLATE_SCHEMA_VERSION;
export const DISPLAY_GAME_PROTOCOL_VERSION = 2 as const;
/** Geriye dönük import adı oyun/Shell protokolünün güncel sürümünü gösterir. */
export const DISPLAY_PROTOCOL_VERSION = DISPLAY_GAME_PROTOCOL_VERSION;
export type DisplayProtocolVersion = typeof DISPLAY_GAME_PROTOCOL_VERSION;

export type DisplayGameEvent =
  | {
      protocolVersion: DisplayProtocolVersion;
      type: 'GAME_READY';
      gameSessionId: string;
      gameId: string;
      maxPlayers: number;
      capabilities: string[];
      /** Oyun, gerçekten uyguladığı Hub belgelerinin hashlerini geri bildirir. */
      appliedVariantHash: string;
      appliedAreaSceneHash?: string | undefined;
      emittedAt: string;
    }
  | {
      protocolVersion: DisplayProtocolVersion;
      type: 'HUD_STATE';
      gameSessionId: string;
      revision: number;
      state: Record<string, unknown>;
      emittedAt: string;
    }
  | {
      protocolVersion: DisplayProtocolVersion;
      type: 'SCORE_EVENT';
      gameSessionId: string;
      eventId: string;
      playerId: string;
      scoreType: string;
      delta: number;
      total: number;
      reason: string;
      emittedAt: string;
    }
  | {
      protocolVersion: DisplayProtocolVersion;
      type: 'ROUND_RESULT';
      gameSessionId: string;
      roundId: string;
      durationSeconds: number;
      standings: Array<{
        playerId: string;
        rank: number;
        score: number;
        outcome?: 'winner' | 'eliminated' | 'finished' | undefined;
      }>;
      emittedAt: string;
    };

export type DisplayShellCommand =
  | { protocolVersion: DisplayProtocolVersion; type: 'SHELL_CONTEXT'; gameSessionId: string; locale: 'tr' | 'en'; players: DisplayPlayer[] }
  | { protocolVersion: DisplayProtocolVersion; type: 'PAUSE_GAME'; reason: string }
  | { protocolVersion: DisplayProtocolVersion; type: 'RESUME_GAME' }
  | { protocolVersion: DisplayProtocolVersion; type: 'END_GAME'; reason: 'rotation' | 'moderator' | 'event-ended' };

export interface DisplayPlayer {
  id: string;
  seat: number;
  displayName: string;
  avatarUrl: string | null;
  connected: boolean;
  ready: boolean;
}

/** Oyundan bağımsız katılım gateway'inin Display Shell'e verdiği olaylar. */
export type DisplayPlayerEvent =
  | { protocolVersion: DisplayProtocolVersion; type: 'PLAYER_JOINED'; eventId: string; playerId: string; connectionId: string; displayName: string; avatarUrl: string | null; emittedAt: string }
  | { protocolVersion: DisplayProtocolVersion; type: 'PLAYER_RECONNECTED'; eventId: string; playerId: string; connectionId: string; emittedAt: string }
  | { protocolVersion: DisplayProtocolVersion; type: 'PLAYER_READY_CHANGED'; eventId: string; playerId: string; ready: boolean; emittedAt: string }
  | { protocolVersion: DisplayProtocolVersion; type: 'PLAYER_DISCONNECTED'; eventId: string; playerId: string; emittedAt: string };

export type DisplayLayerKind = 'game' | 'lobby' | 'players' | 'results' | 'voting' | 'qr' | 'ad' | 'branding' | 'score' | 'timer' | 'logo' | 'sponsor';

export interface DisplayLayer {
  id: string;
  kind: DisplayLayerKind;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  visible: boolean;
  locked?: boolean | undefined;
  settings: Record<string, unknown>;
}

export interface DisplayTemplateDocument {
  protocolVersion: DisplayTemplateSchemaVersion;
  viewport: { width: number; height: number; safeAreaPercent: number };
  theme: { background: string; foreground: string; accent: string; radius: number };
  layers: DisplayLayer[];
}

export type DisplayTemplateStatus = 'draft' | 'published' | 'archived';
export type DisplayTemplateBindingTarget = 'catalog' | 'product' | 'account' | 'endpoint';

export interface DisplayTemplateBinding {
  id: string;
  targetType: DisplayTemplateBindingTarget;
  targetKey: string;
  label?: string | undefined;
}

export interface DisplayTemplateRevision {
  id: string;
  revision: number;
  name: string;
  description: string;
  document: DisplayTemplateDocument;
  contentHash: string;
  createdAt: string;
}

export interface DisplayTemplateItem {
  id: string;
  accountId: string;
  name: string;
  description: string;
  status: DisplayTemplateStatus;
  revision: number;
  publishedRevision: number | null;
  document: DisplayTemplateDocument;
  bindings: DisplayTemplateBinding[];
  contentHash: string;
  createdAt: string;
  updatedAt: string;
}

export type AreaSceneStatus = 'draft' | 'published' | 'archived';
export type AreaSceneNodeKind =
  | 'primitive'
  | 'asset'
  | 'spawn'
  | 'collider'
  | 'trigger'
  | 'gameplay-zone'
  | 'navmesh'
  | 'light'
  | 'camera';

export interface AreaVector3 { x: number; y: number; z: number }
export interface AreaSceneNode {
  id: string;
  name: string;
  kind: AreaSceneNodeKind;
  assetId?: string | undefined;
  primitive?: 'box' | 'sphere' | 'cylinder' | 'plane' | undefined;
  position: AreaVector3;
  rotation: AreaVector3;
  scale: AreaVector3;
  visible: boolean;
  locked: boolean;
  layer: string;
  groupId?: string | undefined;
  material?: {
    color: string;
    emissive: string;
    metalness: number;
    roughness: number;
    opacity: number;
  } | undefined;
  light?: {
    type: 'point' | 'spot' | 'directional';
    color: string;
    intensity: number;
    range: number;
    castShadow: boolean;
  } | undefined;
  camera?: { fov: number; near: number; far: number } | undefined;
  navigation?: { walkable: boolean; areaCost: number } | undefined;
  physics: {
    enabled: boolean;
    body: 'static' | 'dynamic' | 'kinematic';
    mass: number;
    friction?: number | undefined;
    restitution?: number | undefined;
    linearDamping?: number | undefined;
    angularDamping?: number | undefined;
  };
  properties: Record<string, string | number | boolean>;
}
export interface AreaSceneDocument {
  version: 1;
  environment: { background: string; ambientIntensity: number; gridSize: number };
  camera: { position: AreaVector3; target: AreaVector3; fov: number };
  nodes: AreaSceneNode[];
}
export interface AreaSceneItem {
  id: string;
  accountId: string;
  coreProductId: string;
  coreProductName?: string | undefined;
  name: string;
  description: string;
  status: AreaSceneStatus;
  revision: number;
  publishedRevision: number | null;
  document: AreaSceneDocument;
  contentHash: string;
  createdAt: string;
  updatedAt: string;
}
export interface AreaAssetItem {
  id: string;
  accountId: string | null;
  name: string;
  category: string;
  modelUrl: string;
  thumbnailUrl: string | null;
  contentHash: string;
  bytes: number;
  inspection: {
    format: 'glb' | 'gltf';
    nodes: number;
    meshes: number;
    primitives: number;
    vertices: number;
    triangles: number;
    materials: number;
    textures: number;
    images: number;
    animations: number;
    embeddedBytes: number;
    texturePixels: number;
    extensions: string[];
  };
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export type GameVariantStatus = 'draft' | 'published' | 'archived';

/** Bir variant daima tek çekirdek oyundur; oyun sepeti Event'in sorumluluğudur. */
export interface GameVariantItem {
  id: string;
  accountId: string;
  name: string;
  coreProductId: string;
  coreProductName?: string | undefined;
  coreProductSlug?: string | undefined;
  /** Katalogdaki güncel medya; variant içine kopyalanmadığı için değişiklik otomatik yansır. */
  coreProductMedia?: {
    coverUrl: string | null;
    bannerUrl: string | null;
    thumbUrl: string | null;
    revision: string;
  } | undefined;
  gameSettingDefinitions?: DisplayGameSettingDefinition[] | undefined;
  areaSceneId?: string | null | undefined;
  areaSceneName?: string | undefined;
  displayTemplateId: string;
  displayTemplateName?: string | undefined;
  branding: Record<string, unknown>;
  advertising: Record<string, unknown>;
  gameSettings: Record<string, unknown>;
  status: GameVariantStatus;
  revision: number;
  contentHash: string;
  createdAt: string;
  updatedAt: string;
}

export type DisplayEventControlMode = 'automatic' | 'manual' | 'hybrid';
export type DisplayEventRotationMode = 'sequential' | 'shuffle' | 'vote';
export type DisplayEventStatus = 'draft' | 'scheduled' | 'running' | 'paused' | 'completed' | 'cancelled';

export interface EventAdvertisingClip {
  id:string;
  placementId:string;
  mediaId:string;
  assetUrl:string;
  mediaKind:'image'|'video';
  offsetSeconds:number;
  durationSeconds:number;
}

export interface DisplayEventEntry {
  id: string;
  variantId: string;
  position: number;
  enabled: boolean;
  repeatCount: number;
  roundLimit: number | null;
  durationSeconds: number | null;
  advertisingTracks?: EventAdvertisingClip[];
  advertisingConsentGranted?: boolean;
  variant?: GameVariantItem | undefined;
}

export interface DisplayEventItem {
  id: string;
  accountId: string;
  endpointId: string;
  name: string;
  startsAt: string;
  endsAt: string;
  controlMode: DisplayEventControlMode;
  rotationMode: DisplayEventRotationMode;
  status: DisplayEventStatus;
  loop: boolean;
  currentEntryId: string | null;
  stateRevision: number;
  entries: DisplayEventEntry[];
  createdAt: string;
  updatedAt: string;
}

export type DisplayModeratorAction = 'start' | 'pause' | 'resume' | 'next' | 'jump' | 'stop' | 'release-force';

/** Ajanın her heartbeat'te aldığı, kendine yeterli ve sürümlü çalışma planı. */
export interface DisplayRuntimePlan {
  entryStartedAt?:string|null;
  protocolVersion: DisplayProtocolVersion;
  revision: number;
  generatedAt: string;
  event: Pick<DisplayEventItem, 'id' | 'name' | 'startsAt' | 'endsAt' | 'controlMode' | 'rotationMode' | 'loop' | 'status'>;
  activeEntry: DisplayEventEntry | null;
  /** Etkinlik sırası; Shell oylama kartlarını ve dinamik katalog medyasını buradan kurar. */
  playlist: DisplayEventEntry[];
  template: DisplayTemplateItem | null;
  /** Oyun adapter'ına yalnız yayınlanmış, kod içermeyen 3B sahne belgesi iletilir. */
  areaScene: AreaSceneItem | null;
  /** Oyun ayarından bağımsız, kısa ömürlü ve cihaz/event bağlı katılım kapısı. */
  join: {
    qrImageUrl: string;
    expiresAt: string;
  } | null;
  variant: GameVariantItem | null;
  forced: boolean;
}

// ─── Oturumlar (calistirma) ──────────────────────────────────────────────────

export type SessionStatus =
  | 'scheduled'
  | 'running'
  | 'completed'
  /** Kullanici durdurdu — harcanan sure kadar dusulur, gerisi iade edilir. */
  | 'stopped'
  /** Baslatilamadi — kredi DUSULMEZ / iade edilir. */
  | 'failed'
  /** Baslamadan iptal edildi — kredi DUSULMEZ. */
  | 'cancelled';

export interface SessionItem {
  id: string;
  endpointId: string;
  endpointName: string;
  productId: string | null;
  productName: string;
  startsAt: string;
  /** Dakika. Varsayilan 60. */
  durationMinutes: number;
  status: SessionStatus;
  /** Bu oturum icin dusulen kredi. Baslamadiysa 0. */
  creditsSpent: number;
  /** status === 'failed' ise sebebi — arayuz bunu AYNEN gosterir. */
  failureReason: string | null;
  /** Ayni rezervasyonun diger endpoint'lerdeki kardesleri. */
  groupId: string | null;
  seriesId: string | null;
  /** Ajanın kabul edilmiş etkileşimli tur sayısı. */
  actualRounds: number;
  /** Raporlanan toplam katılımcı; kredi hesabını değiştirmez. */
  participantCount: number;
}

/**
 * NOT: istege bagli alanlar `| undefined` ile YAZILI. Sunucu
 * `exactOptionalPropertyTypes` altinda derleniyor; o kipte `x?: string`,
 * `undefined` degerini KABUL ETMEZ ("alan yok" ile "alan var ama undefined"
 * farkli seylerdir). Zod cikti tipleri `undefined` urettigi icin sozlesme
 * bunu acikca kabul etmeli.
 */
export interface RepeatRule {
  kind: 'none' | 'daily' | 'weekly';
  /** weekly icin: 0=Pazar … 6=Cumartesi */
  weekdays?: number[] | undefined;
  /** ISO tarih — bu gune kadar (dahil) tekrar eder. */
  until?: string | undefined;
}

/** Oturum olusturma istegi — maliyet onizlemesi AYNI govdeyle sorulur. */
export interface CreateSessionRequest {
  endpointIds: string[];
  productId: string;
  startsAt: string;
  durationMinutes: number;
  repeat?: RepeatRule | undefined;
}

export type SessionBlockerCode =
  | 'NOT_ENTITLED'
  | 'INCOMPATIBLE_ENDPOINT'
  | 'OUTSIDE_OPERATING_HOURS'
  | 'SCHEDULE_CONFLICT'
  | 'ENDPOINT_ARCHIVED'
  | 'IN_THE_PAST';

/**
 * Onizleme sonucu.
 *
 * NEDEN AYRI UC NOKTA: kullanici "Planla"ya basmadan once ne kadar krediye mal
 * olacagini, hangi ekranin cakistigini ve neyin engel oldugunu gormeli.
 * Sunucu bu hesabi olusturma aninda TEKRAR yapar — onizleme guven kaynagi degil,
 * kolaylik.
 */
export interface SessionPlanPreview {
  /** Olusacak toplam oturum sayisi (tekrar x endpoint). */
  sessionCount: number;
  creditsRequired: number;
  balanceAfter: number;
  affordable: boolean;
  blockers: Array<{
    code: SessionBlockerCode;
    endpointId: string | null;
    endpointName: string | null;
    /** Kullaniciya DOGRUDAN gosterilecek Turkce aciklama. */
    message: string;
    /** SCHEDULE_CONFLICT ise cakisan oturum. */
    conflictingSessionId?: string;
    conflictingStartsAt?: string;
  }>;
  /** Sorunsuz olusturulabilecek oturumlarin ozeti — takvim onizlemesi icin. */
  slots: Array<{
    endpointId: string;
    endpointName: string;
    startsAt: string;
    durationMinutes: number;
  }>;
}

// ─── Cihazlar ────────────────────────────────────────────────────────────────

export interface DeviceItem {
  id: string;
  name: string;
  lastSeenAt: string | null;
  createdAt: string;
  /** Istegi yapan cihazin kendisi mi? Arayuz "bu cihaz" rozetini buna gore koyar. */
  current: boolean;
  installedVersions: Record<string, string>;
}

// ─── Lisans / aktivasyon makbuzu ─────────────────────────────────────────────

/**
 * Sunucunun Ed25519 ile imzaladigi aktivasyon makbuzu.
 *
 * NEDEN VAR: eski `activation.dat` yalnizca SIFRELI, IMZALI degildi — ve
 * sifreleme anahtari makinenin kendi kimliklerinden turetildigi icin kullanici
 * kaydi sifirdan uretip `tier:'unlimited', admin:true` yazabiliyordu. Imzali
 * makbuzda sahtecilik icin GIZLI anahtar gerekir; o da yalnizca sunucudadir.
 */
export interface ActivationReceipt {
  v: 1;
  /** Makbuzun kimligi — iptal listesi icin. */
  receiptId: string;
  accountId: string;
  userId: string;
  /** Cihaz parmak izi (hash'lenmis sinyallerin ozeti). */
  deviceId: string;
  role: Role;
  entitlements: Entitlement[];
  issuedAt: string;
  /** Bu tarihten sonra Cortex yeniden dogrulama ister (cevrimdisi tolerans). */
  expiresAt: string;
}

export interface SignedActivationReceipt {
  /** ActivationReceipt'in JSON'unun base64url hali — IMZA BU DIZE UZERINDEDIR. */
  payload: string;
  /** Ed25519 imzasi, base64url. */
  signature: string;
  /** Desktop Launcher saha ajani icin yalnizca TLS yanitinda tasinan kimlik. */
  agent?: {
    endpointId: string;
    token: string;
    heartbeatSeconds: number;
  };
}

// ─── Dagitim ─────────────────────────────────────────────────────────────────

/**
 * Tek `POST /releases/:id/grant` isteginde uretilecek en fazla jeton sayisi.
 *
 * ── NEDEN BURADA ─────────────────────────────────────────────────────────────
 * Bu sayi bir SOZLESME: istemci indirme planini bu boyutta partilere boler,
 * sunucu bundan buyuk istekleri reddeder. Iki tarafta ayri sabit tutulsaydi
 * biri degistiginde buyuk bir surumun indirmesi "400 Gecersiz istek" ile
 * durur, ve hata mesaji parti boyutundan hic bahsetmezdi.
 *
 * Sinirin kendisi ise yanit boyutu icin: 500 jeton ~100 KB'lik bir cevap
 * demek; 50.000 jeton megabaytlara cikar ve HMAC hesabiyla CPU'yu mesgul eder.
 */
export const GRANT_BATCH_MAX = 500;

/**
 * Istemcinin indirmeden once dogruladigi surum tanimi.
 * `signature`, `manifest`in kanonik JSON'u uzerindedir.
 */
export interface SignedReleaseManifest {
  manifest: ReleaseManifest;
  signature: string;
  signingKeyId: string;
}

/**
 * Manifestteki tek bir PARCA.
 *
 * Alan adlari kisa cunku bu nesne binlerce kez tekrarlaniyor: 5 GB'lik bir urun
 * ~5.000 parca eder. `hash`/`size`/`address` yerine `h`/`s`/`a` kullanmak
 * manifest boyutunu ucte bir kadar kuculttuyor.
 */
export interface ManifestChunk {
  /** DUZ metnin SHA-256'si. Istemci "bu bende var mi?" diye BUNA bakar. */
  h: string;
  /** Duz parca boyutu (bayt). */
  s: number;
  /** Depodaki SIFRELI blogun adresi. Indirme bunu kullanir. */
  a: string;
}

export interface ManifestFile {
  path: string;
  /** Duz dosyanin SHA-256'si — birlestirme sonrasi dogrulama. */
  sha256: string;
  sizeBytes: number;
  executable: boolean;
  /**
   * v2: dosya parcalara bolunmus. Bos/eksikse dosya TEK PARCA olarak
   * `storageSha256` uzerinden inilir (v1 uyumlulugu).
   */
  chunks?: ManifestChunk[] | undefined;
  /** v1 uyumlulugu: parcasiz dosyanin depo adresi. */
  storageSha256?: string | undefined;
  encrypted: boolean;
}

/**
 * Bir ürün sürümünün birlikte kurulması gereken görünmez çalışma zamanı modülü.
 *
 * Bağımlılık bir sürüm aralığı olarak değil, yayın anında çözümlenmiş ve
 * imzanın içine kilitlenmiş sürüm olarak taşınır. Böylece aynı ürün manifesti
 * bugün de aylar sonra da aynı engine/modül baytlarına karşılık gelir.
 */
export interface ReleaseDependency {
  releaseId: string;
  productId: string;
  productSlug: string;
  version: string;
  sequence: number;
  /** Bu kilidin çözüldüğü alt sınır; daha yeni uyumlu modül kabul edilebilir. */
  minVersion: string;
  /** Verilirse bu sürüm ve sonrası uyumsuz sayılır. */
  maxVersionExclusive?: string | null | undefined;
}

export interface ReleaseManifest {
  /**
   * 1 = dosya bazli (tum dosya yeniden iner)
   * 2 = PARCA bazli (yalnizca degisen parcalar iner) — bkz. docs/DELTA-UPDATES.md
   */
  v: 1 | 2;
  /**
   * Surumun kimligi — indirme ve icerik anahtari uclari bunu ister.
   *
   * IMZANIN ICINDE: zarfta tasinsaydi araya giren biri kimligi degistirip
   * istemciyi baska bir surumun anahtarini istemeye yonlendirebilirdi.
   */
  releaseId: string;
  /** Hangi urun — surum karistirmasini engeller. */
  productId: string;
  productSlug: string;
  version: string;
  /** Monoton artan. Istemci kurulu sequence'ten KUCUK olani REDDEDER. */
  sequence: number;
  channel: 'stable' | 'beta';
  createdAt: string;
  minCortexVersion: string | null;
  /** Duz icerigin toplam boyutu. Indirilecek miktar DELTA'ya gore daha azdir. */
  totalBytes: number;
  /** `tool` türündeki paketler müşteri kütüphanesinde kart olarak gösterilmez. */
  componentType?: 'product' | 'engine-module' | undefined;
  /** Yayın anında çözümlenip imzaya kilitlenen engine/modül sürümleri. */
  dependencies?: ReleaseDependency[] | undefined;
  files: ManifestFile[];
  /** v2: parcalama parametreleri. Istemci dogrulama icin bilmek isteyebilir. */
  chunking?:
    | {
        algorithm: 'fastcdc-v1';
        minSize: number;
        avgSize: number;
        maxSize: number;
      }
    | undefined;
}

/** Kisa omurlu indirme izni. Cihaza ve surume baglidir. */
export interface DownloadGrant {
  /** Her dosya icin: storageSha256 -> imzali URL. */
  baseUrl: string;
  token: string;
  expiresAt: string;
}

// ─── Ortak hata bicimi ───────────────────────────────────────────────────────

/**
 * Tum hata cevaplari bu bicimde. `code` istemcinin dallanacagi sabit,
 * `message` kullaniciya GOSTERILEBILIR Turkce metin.
 */
export interface ApiError {
  error: {
    code: string;
    message: string;
    /** Form hatalarinda alan bazli detay. */
    fields?: Record<string, string>;
  };
}

export const ERROR_CODES = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_REUSED: 'TOKEN_REUSED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_ENTITLED: 'NOT_ENTITLED',
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  SCHEDULE_CONFLICT: 'SCHEDULE_CONFLICT',
  DEVICE_LIMIT_REACHED: 'DEVICE_LIMIT_REACHED',
  RATE_LIMITED: 'RATE_LIMITED',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  CONFLICT: 'CONFLICT',
  NOT_FOUND: 'NOT_FOUND',
  INTERNAL: 'INTERNAL',
  DEV_ACCESS_REQUIRED: 'DEV_ACCESS_REQUIRED',
  EXPLICIT_DENY: 'EXPLICIT_DENY',
  DEV_POLICY_REVISION_CONFLICT: 'DEV_POLICY_REVISION_CONFLICT',
  DEV_SELF_GRANT_FORBIDDEN: 'DEV_SELF_GRANT_FORBIDDEN',
  DEV_SUBJECT_INELIGIBLE: 'DEV_SUBJECT_INELIGIBLE',
  DEV_BOOTSTRAP_ALREADY_USED: 'DEV_BOOTSTRAP_ALREADY_USED',
  DEV_POLICY_INVALID: 'DEV_POLICY_INVALID',
  DEV_SCOPE_UNAVAILABLE: 'DEV_SCOPE_UNAVAILABLE',
  DEV_SCOPE_NOT_FOUND: 'DEV_SCOPE_NOT_FOUND',
  DEV_ACCESS_NOT_CONFIGURED: 'DEV_ACCESS_NOT_CONFIGURED',
  WORKFORCE_INVITATION_INVALID: 'WORKFORCE_INVITATION_INVALID',
  WORKFORCE_MEMBER_REVISION_CONFLICT: 'WORKFORCE_MEMBER_REVISION_CONFLICT',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// ─── Sayfalama ───────────────────────────────────────────────────────────────

// Başlangıç seçimi mevcut proje pinini değiştirmez; latest kimliğini sunucu sağlar.
export interface DevEngineReleaseChoice {
  releaseId: string;
  version: string;
  sequence: number;
}

export interface DevEngineProfileRule {
  moduleId: string;
  required: boolean;
  allowedReleaseIds: string[];
  latestReleaseId: string | null;
  defaultReleaseId: string | null;
}

export interface DevEngineCreationProfile {
  categoryId: string;
  tool: string;
  toolVersion: string;
  templateId: string;
  engines: DevEngineProfileRule[];
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
